import { Shimmer, Stack } from "@fluentui/react";
import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { logEvent } from "../../../../helper/AppInsightsHelper";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import { getProductMetricsCardsGraphsSelector } from "../../../../slices/ExploreProducts.slice";
import { useAppSelector } from "../../../../store/hooks";
import CustomDropdown from "../../Common/CustomDropdown";
import { PRODUCT_VIEW_DATA } from "../../Common/ProductView.constants";
import { AggregationType } from "../../Common/ProductView.interfaces";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";
import { DEFAULT_AGGREGATION_TYPE } from "./ProductTelemetryTab.constants";
import { ProductTelemetryTabMetricsCard } from "./ProductTelemetryTabMetricsCard";

export const ProductTelemetryTab = (): React.JSX.Element => {
  const { data, error, isError, isLoading, isSuccess } = useAppSelector(getProductMetricsCardsGraphsSelector);
  const filters: any = useContext(ProductViewFiltersContext);
  const [selectedAggregation, setSelectedAggregation] = useState<AggregationType>(DEFAULT_AGGREGATION_TYPE);
  // Billing model options
  const billingModelOptions = useMemo(
    () => [
      { key: "Licensed", text: "Licensed" },
      { key: "Consumption", text: "Consumption" }
    ],
    []
  );
  // Set 'Licensed' as the default tab explicitly
  const [selectedBillingModel, setSelectedBillingModel] = useState<string>("Licensed");

  const getKustoLink: any = (data: any[], isTenantMetrics: 0 | 1 = 0) =>
    data?.filter((x) => x.isTenantMetrics === String(isTenantMetrics))[0]?.kustoLink;

  // Compute aggregation options based on filtered data for the selected tab
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);

  // Get viewConfig flags from API response
  const showApplicationDropdown = data?.viewConfig?.showApplicationDropdown ?? false;
  const showCloudDropdown = data?.viewConfig?.showCloudDropdown ?? false;

  // Cloud Type state
  const [selectedCloudType, setSelectedCloudType] = useState<string>("");

  const aggregationOptions = useMemo(() => {
    // Use the same filtering as filteredData, but only to collect aggregationType values
    const aggSet = new Set<string>();
    (data?.metricsData || []).forEach((m: any) => {
      (m.data || []).forEach((metric: any) => {
        (metric.data || []).forEach((point: any) => {
          const billingMatch = point.billingModel === selectedBillingModel;
          // Only apply application filter for Consumption billing model
          const appMatch =
            showApplicationDropdown && selectedBillingModel === "Consumption"
              ? selectedApplications.length === 0 || selectedApplications.includes(point.application)
              : true;
          const cloudMatch = showCloudDropdown ? selectedCloudType === "" || point.cloud === selectedCloudType : true;
          if (billingMatch && appMatch && cloudMatch && point.aggregationType) {
            aggSet.add(point.aggregationType);
          }
        });
      });
    });
    return Array.from(aggSet)
      .sort((a, b) => a.localeCompare(b))
      .map((agg) => ({ value: agg, text: agg }));
  }, [data, selectedBillingModel, selectedApplications, selectedCloudType, showApplicationDropdown, showCloudDropdown]);

  // Set default aggregation when options change
  React.useEffect(() => {
    if (aggregationOptions.length > 0) {
      if (!aggregationOptions.some((opt) => opt.value === selectedAggregation)) {
        setSelectedAggregation(aggregationOptions[0].value as AggregationType);
      }
    }
  }, [aggregationOptions, selectedAggregation]);

  const handleAggregationSelect = (option: any) => {
    if (option?.optionValue) {
      setSelectedAggregation(option.optionValue as AggregationType);
    }
  };

  // Application dropdown logic (based on showApplicationDropdown flag and Consumption billing model)
  const applicationOptions = useMemo(() => {
    if (!showApplicationDropdown || selectedBillingModel !== "Consumption") return [];
    const appSet = new Set<string>();
    (data?.metricsData || []).forEach((m: any) => {
      (m.data || []).forEach((metric: any) => {
        (metric.data || []).forEach((point: any) => {
          // Only include applications from Consumption billing model
          if (point.billingModel === "Consumption" && point.application) {
            appSet.add(point.application);
          }
        });
      });
    });
    // Sort with "AllUp" first, then alphabetically
    return Array.from(appSet)
      .sort((a, b) => {
        if (a === "AllUp") return -1;
        if (b === "AllUp") return 1;
        return a.localeCompare(b);
      })
      .map((app) => ({ value: app, text: app }));
  }, [data, showApplicationDropdown, selectedBillingModel]);

  // Cloud Type dropdown logic (based on showCloudDropdown flag)
  const cloudTypeOptions = useMemo(() => {
    if (!showCloudDropdown) return [];
    const cloudSet = new Set<string>();
    (data?.metricsData || []).forEach((m: any) => {
      (m.data || []).forEach((metric: any) => {
        (metric.data || []).forEach((point: any) => {
          if (point.cloud) cloudSet.add(point.cloud);
        });
      });
    });
    // Sort with "AllUp" first, then alphabetically
    return Array.from(cloudSet)
      .sort((a, b) => {
        if (a === "AllUp") return -1;
        if (b === "AllUp") return 1;
        return a.localeCompare(b);
      })
      .map((cloud) => ({ value: cloud, text: cloud }));
  }, [data, showCloudDropdown]);

// Set default application when options change (AllUp first if available)
  React.useEffect(() => {
    if (applicationOptions.length > 0) {
      const validSelections = selectedApplications.filter((selectedApp) =>
        applicationOptions.some((opt) => opt.value === selectedApp)
      );
      if (validSelections.length === 0) {
        const allUpOption = applicationOptions.find((opt) => opt.value === "AllUp");
        setSelectedApplications(allUpOption ? [allUpOption.value] : [applicationOptions[0].value]);
      } else if (validSelections.length !== selectedApplications.length) {
        setSelectedApplications(validSelections);
      }
    } else if (selectedApplications.length > 0) {
      setSelectedApplications([]);
    }
  }, [applicationOptions, selectedApplications]);

  // Set default cloud type when options change (AllUp first if available)
  React.useEffect(() => {
    if (cloudTypeOptions.length > 0) {
      if (!cloudTypeOptions.some((opt) => opt.value === selectedCloudType)) {
        const allUpOption = cloudTypeOptions.find((opt) => opt.value === "AllUp");
        setSelectedCloudType(allUpOption ? allUpOption.value : cloudTypeOptions[0].value);
      }
    } else {
      setSelectedCloudType("");
    }
  }, [cloudTypeOptions, selectedCloudType]);

  const handleApplicationSelect = (option: any) => {
    const selected = option?.selectedOptions || [];
    if (selected.includes("AllUp") && selected.length > 1) {
      if (option?.optionValue === "AllUp") {
        setSelectedApplications(["AllUp"]);
      } else {
        setSelectedApplications(selected.filter((app: string) => app !== "AllUp"));
      }
      return;
    }
    setSelectedApplications(selected);
  };

  const handleCloudTypeSelect = (option: any) => {
    if (option?.optionValue) {
      setSelectedCloudType(option.optionValue);
    }
  };

  // Filter data based on billing model, application, and cloud type at the innermost data point level
  const filteredData = useMemo(
    () => ({
      ...data,
      metricsData: (data?.metricsData || [])
        .map((m: any) => ({
          ...m,
          data: (m.data || [])
            .map((metric: any) => {
              // First filter the data points
              const filteredPoints = (metric.data || []).filter((point: any) => {
                const billingMatch = point.billingModel === selectedBillingModel;
                // Only apply application filter for Consumption billing model
                const appMatch =
                  showApplicationDropdown && selectedBillingModel === "Consumption"
                    ? selectedApplications.length === 0 || selectedApplications.includes(point.application)
                    : true;
                const cloudMatch = showCloudDropdown ? !selectedCloudType || point.cloud === selectedCloudType : true;
                return billingMatch && appMatch && cloudMatch;
              });

              // Group by snapshot date (x) and sum y values for duplicates
              const dateMap = new Map<string, any>();
              filteredPoints.forEach((point: any) => {
                const dateKey = point.x;
                if (dateMap.has(dateKey)) {
                  // Sum the y value for duplicate dates
                  const existingPoint = dateMap.get(dateKey);
                  existingPoint.y = (Number(existingPoint.y) || 0) + (Number(point.y) || 0);
                } else {
                  // First occurrence - clone the point
                  dateMap.set(dateKey, { ...point });
                }
              });

              // Convert map back to array
              const summedData = Array.from(dateMap.values());

              return {
                ...metric,
                data: summedData
              };
            })
            .filter((metric: any) => (metric.data || []).length > 0)
        }))
        .filter((m: any) => (m.data || []).length > 0),
      cardDataValues: data?.cardDataValues || []
    }),
    [data, selectedBillingModel, selectedApplications, selectedCloudType, showApplicationDropdown, showCloudDropdown]
  );
  // Check if dropdown section should be shown based on viewConfig flag
  const showAggregationDropdown = data?.viewConfig?.showDailyAggregation ?? false;

  useEffect(() => {
    const props = {
      productName: filters?.productName,
      productFilters: JSON.stringify(filters)
    };
    logEvent("Product Telemetry", "Product View Page", "ProductTelemetryTab", props);
  }, [filters]);

  // Set default billing model based on data:
  useEffect(() => {
    // Check if Consumption has data
    let hasConsumptionData = false;
    let hasLicensedData = false;
    (data?.metricsData || []).forEach((m: any) => {
      (m.data || []).forEach((metric: any) => {
        (metric.data || []).forEach((point: any) => {
          if (point.billingModel === "Consumption") {
            hasConsumptionData = true;
          } else if (point.billingModel === "Licensed") {
            hasLicensedData = true;
          }
        });
      });
    });

    // Determine default tab based on data
    if (hasConsumptionData && !hasLicensedData) {
      setSelectedBillingModel("Consumption");
    } else {
      setSelectedBillingModel("Licensed");
    }
  }, [filters?.productName, filters?.productFamily, data]);

  // Determine which billing model tabs should be disabled
  const billingModelTabDisabled: Record<string, boolean> = useMemo(() => {
    const result: Record<string, boolean> = {};
    billingModelOptions.forEach((bm) => {
      let hasData = false;
      (data?.metricsData || []).forEach((m: any) => {
        (m.data || []).forEach((metric: any) => {
          (metric.data || []).forEach((point: any) => {
            if (point.billingModel === bm.key) {
              hasData = true;
            }
          });
        });
      });
      result[bm.key] = !hasData;
    });
    return result;
  }, [data, billingModelOptions]);

  // Custom tab bar for billing model selection
  const renderBillingModelTabs = () => (
    <div style={{ display: "inline-flex", gap: 8, marginBottom: 8 }}>
      {billingModelOptions.map((bm) => {
        const isDisabled = billingModelTabDisabled[bm.key];
        const isSelected = selectedBillingModel === bm.key;
        return (
          <button
            key={bm.key}
            onClick={() => !isDisabled && setSelectedBillingModel(bm.key)}
            disabled={isDisabled}
            style={{
              padding: "6px 20px",
              border: isSelected ? "1px solid #0078D4" : "1px solid #E1DFDD",
              borderRadius: "20px",
              backgroundColor: isSelected ? "#E3F2FD" : "#FFFFFF",
              color: isSelected ? "#0078D4" : isDisabled ? "#A19F9D" : "#605E5C",
              cursor: isDisabled ? "not-allowed" : "pointer",
              fontSize: "13px",
              fontWeight: isSelected ? 600 : 400,
              opacity: isDisabled ? 0.5 : 1,
              transition: "all 0.2s ease",
              outline: "none"
            }}
            title={isDisabled ? `No data for ${bm.text}` : bm.text}
          >
            {bm.text}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      {renderBillingModelTabs()}
      {showAggregationDropdown && (
        <div
          style={{
            paddingTop: 10,
            paddingBottom: 10,
            paddingLeft: 10,
            display: "flex",
            flexBasis: "100%",
            gap: 16
          }}
        >
          {isLoading ? (
            <div style={{ width: 200 }}>
              <Shimmer
                styles={{
                  shimmerWrapper: [{ backgroundColor: "#deecf9", height: 32, borderRadius: "4px" }],
                  shimmerGradient: [
                    {
                      backgroundColor: "#deecf9",
                      backgroundImage:
                        "linear-gradient(to right, rgba(255,255,255,0) 0%, #c7e0f4 50%, rgba(255,255,255,0) 100%)"
                    }
                  ]
                }}
              />
            </div>
          ) : (
            <div style={{ display: "flex", gap: 16 }}>
              <CustomDropdown
                label="Aggregation"
                data={aggregationOptions}
                defaultOption={
                  selectedAggregation
                    ? {
                        optionText: selectedAggregation,
                        optionValue: selectedAggregation,
                        selectedOptions: [selectedAggregation]
                      }
                    : undefined
                }
                onSelectedOption={handleAggregationSelect}
                styles={{ root: { minWidth: 140 } }}
              />
              {showApplicationDropdown && selectedBillingModel === "Consumption" && applicationOptions.length > 0 && (
                <CustomDropdown
                  label="Application Type"
                  data={applicationOptions}
                  defaultOption={
                    selectedApplications.length > 0
                      ? {
                          optionText: selectedApplications.includes("AllUp")
                            ? "AllUp"
                            : selectedApplications.join(", "),
                          optionValue: selectedApplications[0],
                          selectedOptions: selectedApplications
                        }
                      : undefined
                  }
                  onSelectedOption={handleApplicationSelect}
                  isMultiSelect
                  styles={{ root: { width: 180, minWidth: 180, maxWidth: 180 } }}
                />
              )}
              {showCloudDropdown && cloudTypeOptions.length > 0 && (
                <CustomDropdown
                  label="Cloud Type"
                  data={cloudTypeOptions}
                  defaultOption={
                    selectedCloudType
                      ? {
                          optionText: selectedCloudType,
                          optionValue: selectedCloudType,
                          selectedOptions: [selectedCloudType]
                        }
                      : undefined
                  }
                  onSelectedOption={handleCloudTypeSelect}
                  styles={{ root: { minWidth: 180 } }}
                />
              )}
            </div>
          )}
        </div>
      )}
      {/* Special case: Microsoft Purview Consumption, productid=72 - categorize by SolutionCategory */}
      {filters?.productFamily === "Microsoft Purview Consumption" ? (
        (() => {
          // Extract unique SolutionCategory values from filtered data
          const solutionCategories = new Set<string>();
          (filteredData?.metricsData || []).forEach((m: any) => {
            (m.data || []).forEach((metric: any) => {
              (metric.data || []).forEach((point: any) => {
                if (point.solutionCategory) {
                  solutionCategories.add(point.solutionCategory);
                }
              });
            });
          });

          const categories = Array.from(solutionCategories).sort();

          return categories.length > 0 ? (
            categories.map((category) => {
              // Filter data for this SolutionCategory
              const categoryFilteredData = {
                ...filteredData,
                metricsData: (filteredData?.metricsData || [])
                  .map((m: any) => ({
                    ...m,
                    data: (m.data || [])
                      .map((metric: any) => {
                        // Filter by solution category
                        const filteredPoints = (metric.data || []).filter(
                          (point: any) => point.solutionCategory === category
                        );

                        // Group by snapshot date (x) and sum y values for duplicates
                        const dateMap = new Map<string, any>();
                        filteredPoints.forEach((point: any) => {
                          const dateKey = point.x;
                          if (dateMap.has(dateKey)) {
                            // Sum the y value for duplicate dates
                            const existingPoint = dateMap.get(dateKey);
                            existingPoint.y = (Number(existingPoint.y) || 0) + (Number(point.y) || 0);
                          } else {
                            // First occurrence - clone the point
                            dateMap.set(dateKey, { ...point });
                          }
                        });

                        // Convert map back to array
                        const summedData = Array.from(dateMap.values());

                        return {
                          ...metric,
                          data: summedData
                        };
                      })
                      .filter((metric: any) => (metric.data || []).length > 0)
                  }))
                  .filter((m: any) => (m.data || []).length > 0)
              };

              return (
                <div key={category} style={{ width: "100%", marginBottom: 32 }}>
                  <h3 style={PRODUCT_VIEW_STYLES.TAB.SUMMARY.headerStyles}>{category}</h3>
                  <Stack horizontal tokens={PRODUCT_VIEW_STYLES.TAB.SUMMARY.stackContainer}>
                    {PRODUCT_VIEW_DATA.TABS.TAB.SUMMARY.DATA.map(({ key, header, isTenantMetrics }: any) => (
                      <ProductTelemetryTabMetricsCard
                        key={`${category}-${key}`}
                        header={header}
                        isTenantMetrics={isTenantMetrics}
                        data={categoryFilteredData}
                        isError={isError}
                        isLoading={isLoading}
                        isSuccess={isSuccess}
                        kustoLink={getKustoLink(categoryFilteredData?.metricsData || [], isTenantMetrics)}
                        productName={filters?.productName}
                        selectedAggregation={selectedAggregation}
                      />
                    ))}
                  </Stack>
                </div>
              );
            })
          ) : (
            // Fallback if no categories found
            <Stack horizontal tokens={PRODUCT_VIEW_STYLES.TAB.SUMMARY.stackContainer}>
              {PRODUCT_VIEW_DATA.TABS.TAB.SUMMARY.DATA.map(({ key, header, isTenantMetrics }: any) => (
                <ProductTelemetryTabMetricsCard
                  key={key}
                  header={header}
                  isTenantMetrics={isTenantMetrics}
                  data={filteredData}
                  isError={isError}
                  isLoading={isLoading}
                  isSuccess={isSuccess}
                  kustoLink={getKustoLink(filteredData?.metricsData || [], isTenantMetrics)}
                  productName={filters?.productName}
                  selectedAggregation={selectedAggregation}
                />
              ))}
            </Stack>
          );
        })()
      ) : (
        // Default case: render all cards together without categorization
        <Stack horizontal tokens={PRODUCT_VIEW_STYLES.TAB.SUMMARY.stackContainer}>
          {PRODUCT_VIEW_DATA.TABS.TAB.SUMMARY.DATA.map(({ key, header, isTenantMetrics }: any) => (
            <ProductTelemetryTabMetricsCard
              key={key}
              error={error}
              header={header}
              isTenantMetrics={isTenantMetrics}
              data={filteredData}
              isError={isError}
              isLoading={isLoading}
              isSuccess={isSuccess}
              kustoLink={getKustoLink(filteredData?.metricsData || [], isTenantMetrics)}
              productName={filters?.productName}
              selectedAggregation={selectedAggregation}
            />
          ))}
        </Stack>
      )}
    </>
  );
};

export const tenantCountMetricsTooltipMessage = (
  <Fragment>
    See number of tenants are trending over time for security products. The graph below shows 6 month trend.
    <br />
    <b>
      <i>Note: Filters are not supported for this graph.</i>
    </b>
  </Fragment>
);
